<template>
  <div
    v-if="taskStore.isTracking"
    class="qgen-floating"
    :style="floatingStyle"
  >
    <button
      class="qgen-close"
      title="关闭任务提示"
      @mousedown.stop
      @click.stop="handleClose"
    >
      <el-icon><Close /></el-icon>
    </button>

    <div
      class="qgen-ball"
      :class="`is-${taskStore.status || 'unknown'}`"
      :style="{ '--qgen-percent': `${progressPercent}%` }"
      title="双击查看生成状态"
      @mousedown="startDrag"
      @click="togglePopover"
      @dblclick.stop="openPanel"
    >
      <span class="qgen-ball-text">{{ ballText }}</span>
    </div>

    <transition name="qgen-pop">
      <div v-if="popoverOpen" class="qgen-popover">
        <div class="qgen-popover-title">{{ statusText }}</div>
        <div class="qgen-popover-line">{{ taskStore.message || '任务状态同步中' }}</div>
        <div v-if="taskStore.progress.current_chapter" class="qgen-popover-line muted">
          {{ taskStore.progress.current_chapter }}
        </div>
        <div class="qgen-popover-meta">
          <span>已生成 {{ taskStore.generatedCount }} 题</span>
          <span v-if="taskStore.failedCount">失败 {{ taskStore.failedCount }}</span>
        </div>
        <el-button size="small" type="primary" plain @click.stop="openPanel">查看详情</el-button>
      </div>
    </transition>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { ElMessageBox } from 'element-plus'
import { Close } from '@element-plus/icons-vue'
import { useQuestionGenTaskStore } from '@/store'

export default {
  name: 'QuestionGenFloatingWidget',
  components: {
    Close
  },
  setup() {
    const taskStore = useQuestionGenTaskStore()
    const popoverOpen = ref(false)
    const dragging = ref(false)
    const dragMoved = ref(false)
    const dragStart = ref({ x: 0, y: 0, left: 24, bottom: 24 })

    const progressPercent = computed(() => {
      const percent = Number(taskStore.progress?.percent || 0)
      return Math.max(0, Math.min(100, percent))
    })

    const floatingStyle = computed(() => ({
      left: `${taskStore.bubblePosition.left}px`,
      bottom: `${taskStore.bubblePosition.bottom}px`
    }))

    const statusText = computed(() => {
      const map = {
        queued: '排队中',
        preflighting: '预审中',
        splitting: '切分中',
        normalizing: '规范中',
        answering: '补答案中',
        reviewing: '复核中',
        validating: '校验中',
        ready_for_manual_review: '待确认',
        committing: '入库中',
        succeeded: '处理完成',
        partially_succeeded: '部分完成',
        failed: '处理失败',
        cancelling: '正在结束',
        cancelled: '已结束'
      }
      return map[taskStore.status] || '生成任务'
    })

    const ballText = computed(() => {
      if (taskStore.status === 'ready_for_manual_review') return '待审'
      if (taskStore.status === 'succeeded') return '完成'
      if (taskStore.status === 'partially_succeeded') return '待审'
      if (taskStore.status === 'failed') return '失败'
      if (taskStore.status === 'cancelled') return '结束'
      const total = Number(taskStore.progress?.total || 0)
      const current = Number(taskStore.progress?.current || 0)
      if (total) return `${current}/${total}`
      return '生成'
    })

    const clampPosition = (left, bottom) => {
      const width = typeof window !== 'undefined' ? window.innerWidth : 1200
      const height = typeof window !== 'undefined' ? window.innerHeight : 800
      return {
        left: Math.max(8, Math.min(left, width - 72)),
        bottom: Math.max(8, Math.min(bottom, height - 72))
      }
    }

    const onMouseMove = (event) => {
      if (!dragging.value) return
      const dx = event.clientX - dragStart.value.x
      const dy = event.clientY - dragStart.value.y
      if (Math.abs(dx) + Math.abs(dy) > 5) {
        dragMoved.value = true
        popoverOpen.value = false
      }
      const next = clampPosition(dragStart.value.left + dx, dragStart.value.bottom - dy)
      taskStore.setBubblePosition(next)
    }

    const onMouseUp = () => {
      dragging.value = false
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
      window.setTimeout(() => {
        dragMoved.value = false
      }, 0)
    }

    const startDrag = (event) => {
      if (event.button !== 0) return
      dragging.value = true
      dragMoved.value = false
      dragStart.value = {
        x: event.clientX,
        y: event.clientY,
        left: taskStore.bubblePosition.left,
        bottom: taskStore.bubblePosition.bottom
      }
      window.addEventListener('mousemove', onMouseMove)
      window.addEventListener('mouseup', onMouseUp)
    }

    const togglePopover = () => {
      if (dragMoved.value) return
      popoverOpen.value = !popoverOpen.value
    }

    const openPanel = () => {
      popoverOpen.value = false
      taskStore.openPanel()
    }

    const handleClose = async () => {
      if (taskStore.isRunning) {
        try {
          await ElMessageBox.confirm(
            '结束后不会继续生成新题，已生成的题目会保留在任务面板中。',
            '结束本次生成？',
            {
              confirmButtonText: '结束生成',
              cancelButtonText: '继续生成',
              type: 'warning'
            }
          )
        } catch (error) {
          return
        }
        await taskStore.cancelTask()
        taskStore.openPanel()
        return
      }
      taskStore.clearTask()
    }

    const handleResize = () => {
      taskStore.setBubblePosition(clampPosition(taskStore.bubblePosition.left, taskStore.bubblePosition.bottom))
    }

    onMounted(() => {
      taskStore.hydrate()
      window.addEventListener('resize', handleResize)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    })

    return {
      taskStore,
      popoverOpen,
      progressPercent,
      floatingStyle,
      statusText,
      ballText,
      startDrag,
      togglePopover,
      openPanel,
      handleClose
    }
  }
}
</script>

<style scoped>
.qgen-floating {
  position: fixed;
  z-index: 3000;
  width: 60px;
  height: 60px;
  user-select: none;
}

.qgen-ball {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  box-shadow: 0 12px 30px rgba(31, 41, 55, 0.24);
  background:
    radial-gradient(circle at 50% 50%, #409eff 0 56%, transparent 57%),
    conic-gradient(#67c23a var(--qgen-percent), rgba(255, 255, 255, 0.85) 0);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.qgen-ball:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 34px rgba(31, 41, 55, 0.28);
}

.qgen-ball:active {
  cursor: grabbing;
}

.qgen-ball.is-queued,
.qgen-ball.is-preflighting,
.qgen-ball.is-splitting,
.qgen-ball.is-normalizing,
.qgen-ball.is-answering,
.qgen-ball.is-reviewing,
.qgen-ball.is-validating,
.qgen-ball.is-committing {
  animation: qgenPulse 1.5s ease-in-out infinite;
}

.qgen-ball.is-succeeded {
  background: #67c23a;
}

.qgen-ball.is-ready_for_manual_review,
.qgen-ball.is-partially_succeeded {
  background: #e6a23c;
}

.qgen-ball.is-failed {
  background: #f56c6c;
}

.qgen-ball.is-cancelling {
  background: #e6a23c;
  animation: qgenPulse 1.2s ease-in-out infinite;
}

.qgen-ball.is-cancelled {
  background: #909399;
}

.qgen-ball-text {
  max-width: 44px;
  text-align: center;
  line-height: 1.1;
  word-break: keep-all;
}

.qgen-close {
  position: absolute;
  top: -7px;
  right: -7px;
  z-index: 2;
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #606266;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(31, 41, 55, 0.2);
}

.qgen-popover {
  position: absolute;
  left: 0;
  bottom: 72px;
  width: 240px;
  padding: 12px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.16);
}

.qgen-popover-title {
  margin-bottom: 6px;
  color: #303133;
  font-size: 14px;
  font-weight: 700;
}

.qgen-popover-line {
  color: #606266;
  font-size: 12px;
  line-height: 1.45;
  word-break: break-word;
}

.qgen-popover-line.muted {
  margin-top: 4px;
  color: #909399;
}

.qgen-popover-meta {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin: 10px 0;
  color: #909399;
  font-size: 12px;
}

.qgen-pop-enter-active,
.qgen-pop-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.qgen-pop-enter-from,
.qgen-pop-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

@keyframes qgenPulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.04);
  }
}
</style>
